/**
 * Şirket konumu seçicisinin adres arama / koordinat çözme yardımcıları.
 *
 * Servis: OpenStreetMap Nominatim — API anahtarı istemiyor ve harita
 * karolarını zaten OSM'den alıyoruz. Kullanım politikası saniyede 1 istek
 * sınırı koyuyor, bu yüzden arama yalnızca kullanıcı Enter'a bastığında,
 * ters çözümleme de gecikmeli olarak tetiklenir — tuş başına istek YOK.
 */

export interface GeocodeHit {
  label: string;
  latitude: number;
  longitude: number;
}

const NOMINATIM = "https://nominatim.openstreetmap.org";
const COUNTRY = "tr";

/**
 * Serbest metinden koordinat çıkarır. Üç biçimi tanır:
 *   "40.995131, 29.038142"                         → düz koordinat
 *   "https://maps.google.com/...@40.99,29.03,17z"  → Google Maps bağlantısı
 *   "https://...?q=40.99,29.03"                    → paylaş bağlantısı
 *
 * Türkiye'de kimse haritada elle yer aramıyor; Google Maps'ten kopyalayıp
 * yapıştırıyor. Yapıştırılan metin koordinat içeriyorsa ağa hiç çıkmayız.
 */
export const parseCoordinates = (input: string): GeocodeHit | null => {
  const text = input.trim();
  if (!text) return null;

  const patterns = [
    /@(-?\d{1,3}\.\d+),\s*(-?\d{1,3}\.\d+)/, // Google Maps URL'i
    /[?&]q=(-?\d{1,3}\.\d+),\s*(-?\d{1,3}\.\d+)/, // paylaş bağlantısı
    /^(-?\d{1,3}\.\d+)[,\s]+(-?\d{1,3}\.\d+)$/, // düz "lat, lng"
  ];

  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (!match) continue;
    const latitude = Number(match[1]);
    const longitude = Number(match[2]);
    if (!isValidLatLng(latitude, longitude)) continue;
    return {
      label: `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`,
      latitude,
      longitude,
    };
  }
  return null;
};

export const isValidLatLng = (latitude: number, longitude: number): boolean =>
  Number.isFinite(latitude) &&
  Number.isFinite(longitude) &&
  Math.abs(latitude) <= 90 &&
  Math.abs(longitude) <= 180;

/** Adres metnini konum adaylarına çevirir. Hata durumunda boş liste döner. */
export const searchAddress = async (
  query: string,
  signal?: AbortSignal
): Promise<GeocodeHit[]> => {
  const params = new URLSearchParams({
    q: query,
    format: "jsonv2",
    limit: "5",
    countrycodes: COUNTRY,
    "accept-language": "tr",
  });

  const response = await fetch(`${NOMINATIM}/search?${params}`, { signal });
  if (!response.ok) throw new Error(`Nominatim ${response.status}`);

  const data = await response.json();
  return (Array.isArray(data) ? data : [])
    .map((hit: any) => ({
      label: hit.display_name as string,
      latitude: Number(hit.lat),
      longitude: Number(hit.lon),
    }))
    .filter((hit: GeocodeHit) => isValidLatLng(hit.latitude, hit.longitude));
};

/**
 * Koordinatın okunabilir adresini döndürür — seçilen noktanın doğru yer olup
 * olmadığını koordinata bakarak anlamak mümkün değil.
 */
export const reverseGeocode = async (
  latitude: number,
  longitude: number,
  signal?: AbortSignal
): Promise<string | null> => {
  const params = new URLSearchParams({
    lat: String(latitude),
    lon: String(longitude),
    format: "jsonv2",
    zoom: "18",
    "accept-language": "tr",
  });

  const response = await fetch(`${NOMINATIM}/reverse?${params}`, { signal });
  if (!response.ok) return null;

  const data = await response.json();
  return (data?.display_name as string) ?? null;
};
